import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { cdnSrcSet, cdnVariant } from '../lib/cdn.ts';
import type { PageImage } from '../lib/types.ts';
import { zoomScrollOffset } from './imageZoom.ts';

export interface ImageLightboxProps {
  /** The image to show. `originalSrc` (the uncropped upload) wins over the cropped `src`. */
  readonly image: PageImage;
  readonly onClose: () => void;
  /** Step to the previous image. Omitted where the overlay shows a single image (maps, content). */
  readonly onPrev?: () => void;
  /** Step to the next image. */
  readonly onNext?: () => void;
  /** "3 af 23", shown under the image when stepping through a set. */
  readonly counter?: string;
}

/**
 * Full-screen image overlay with a close icon in the top-right corner, used by the sections that
 * let the visitor open an image in a larger version (maps and other detailed graphics).
 *
 * - Fits the whole image on screen by default (`object-contain`), so nothing is cropped.
 * - Clicking the image toggles a zoomed, scrollable view — a map fitted to a phone screen is
 *   otherwise too small to read.
 * - Closes on Escape, on the close button, and on a click outside the image.
 * - Locks page scroll while open and returns focus to whatever opened it.
 * - With `onPrev`/`onNext` it also steps through a set (the photo wall), with arrow keys and round
 *   nav buttons. Without them it behaves exactly as before — a single image, no extra chrome.
 */
export function ImageLightbox({ image, onClose, onPrev, onNext, counter }: ImageLightboxProps) {
  const [zoomed, setZoomed] = useState(false);
  // Where on the image the visitor asked to zoom, as 0–1 fractions of its displayed size. Kept
  // as fractions because the image changes size the moment we zoom, so pixel offsets would be
  // measured against the wrong box.
  const [zoomPoint, setZoomPoint] = useState<{ x: number; y: number } | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleClose = useCallback(() => onClose(), [onClose]);

  function toggleZoom(event: React.MouseEvent<HTMLImageElement>) {
    if (zoomed) {
      setZoomed(false);
      setZoomPoint(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setZoomPoint({
      x: (event.clientX - rect.left) / rect.width,
      y: (event.clientY - rect.top) / rect.height,
    });
    setZoomed(true);
  }

  // Scroll the zoomed image so the spot that was clicked is what you actually end up looking at.
  // Without this the scroll container starts at 0,0 and every zoom lands in the top-left corner.
  // Runs in a layout effect so it happens before paint — no visible jump from corner to target.
  useLayoutEffect(() => {
    const container = scrollRef.current;
    if (!zoomed || !zoomPoint || !container) return;
    container.scrollLeft = zoomScrollOffset(zoomPoint.x, container.scrollWidth, container.clientWidth);
    container.scrollTop = zoomScrollOffset(zoomPoint.y, container.scrollHeight, container.clientHeight);
  }, [zoomed, zoomPoint]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose();
      }
      // Only while stepping through a set — the arrow keys stay free to scroll a zoomed map.
      if (event.key === 'ArrowLeft' && onPrev) {
        event.stopPropagation();
        onPrev();
      }
      if (event.key === 'ArrowRight' && onNext) {
        event.stopPropagation();
        onNext();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose, onPrev, onNext]);

  // Keep the page behind the overlay from scrolling, and hand focus to the close button so the
  // overlay is reachable (and dismissable) from the keyboard.
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, []);

  // The uncropped original when the BackOffice published one; otherwise the largest rendition of
  // the cropped image we already have.
  const fullSrc = image.originalSrc ?? cdnVariant(image.src, 'desktop') ?? image.src;
  const srcSet = image.originalSrc ? null : cdnSrcSet(image.src);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={image.alt || 'Billede i fuld skærm'}
      onClick={handleClose}
      className="fixed inset-0 z-100 bg-mf-black/95 flex items-center justify-center p-3 sm:p-6"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={handleClose}
        aria-label="Luk"
        className="absolute top-3 right-3 sm:top-5 sm:right-5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur transition-colors hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </svg>
      </button>

      <div
        ref={scrollRef}
        // Stop the backdrop handler from firing for clicks that land on the image itself.
        onClick={e => e.stopPropagation()}
        className={
          zoomed
            ? 'h-full w-full overflow-auto overscroll-contain'
            : 'flex h-full w-full items-center justify-center'
        }
      >
        <img
          src={fullSrc}
          srcSet={srcSet ?? undefined}
          sizes="100vw"
          alt={image.alt}
          onClick={toggleZoom}
          className={
            zoomed
              ? 'min-w-full w-auto max-w-none cursor-zoom-out'
              : 'max-h-full max-w-full object-contain cursor-zoom-in'
          }
        />
      </div>

      {(onPrev || onNext) && !zoomed && (
        <>
          <LightboxNav side="left" label="Forrige billede" onClick={onPrev} />
          <LightboxNav side="right" label="Næste billede" onClick={onNext} />
          <div
            onClick={e => e.stopPropagation()}
            className="absolute inset-x-0 bottom-0 px-6 pb-5 pt-10 text-center bg-linear-to-t from-mf-black/70 to-transparent pointer-events-none"
          >
            {image.alt && (
              <p className="mx-auto max-w-[60ch] text-sm sm:text-base text-white/90">{image.alt}</p>
            )}
            {counter && <p className="mt-1 text-xs text-white/55">{counter}</p>}
          </div>
        </>
      )}
    </div>
  );
}

function LightboxNav({ side, label, onClick }: { side: 'left' | 'right'; label: string; onClick?: () => void }) {
  if (!onClick) return null;
  return (
    <button
      type="button"
      aria-label={label}
      onClick={e => { e.stopPropagation(); onClick(); }}
      className={`absolute top-1/2 -translate-y-1/2 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-mf-coral text-white shadow-md transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${side === 'left' ? 'left-2 sm:left-5' : 'right-2 sm:right-5'}`}
    >
      <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {side === 'left' ? <><path d="M19 12H5" /><path d="M11 6l-6 6 6 6" /></> : <><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></>}
      </svg>
    </button>
  );
}
